import {
  ListingCarousel_ListingFragment,
  SearchUseCase,
} from "@outschool/gql-frontend-generated";
import {
  ApolloClient,
  useApolloClient,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { searchListingsQuery } from "@outschool/ui-components-website";
import React from "react";

import { useActiveLearner } from "../providers/ActiveLearnerProvider";
import { useTimeZone } from "../providers/TimeZoneProvider";
import { useActiveLearnerSearchFilters } from "./useActiveLearnerSearchFilters";
import { FeaturedCategory } from "./useParentDashboardFeaturedCategories";

const getNestedData = (data: any, path: string) => {
  const pathArray = path.split(".");
  return pathArray.reduce((data, key) => data?.[key], data) ?? [];
};

type Props = {
  selectedCategory: FeaturedCategory;
  preloadCategories: FeaturedCategory[];
  limit?: number;
};

type ParentDashboardFeaturedQueriesResults = Record<
  string,
  { listings: ListingCarousel_ListingFragment[]; searchUid?: string }
>;
const useParentDashboardFeaturedQueries = ({
  selectedCategory,
  preloadCategories,
  limit = 10,
}: Props) => {
  const client = useApolloClient();
  const { timeZone } = useTimeZone();
  const [results, setResults] =
    React.useState<ParentDashboardFeaturedQueriesResults>({});
  const { activeLearner } = useActiveLearner({
    onActiveLearnerChanged() {
      setResults({});
    },
  });
  const getActiveLearnerFilters = useActiveLearnerSearchFilters();

  const isLoaded = React.useCallback(
    (category?: FeaturedCategory) => Boolean(category && results[category.id]),
    [results],
  );

  const { loading, data } = useQueryWithPreviousData(selectedCategory?.query, {
    skip:
      !selectedCategory?.query ||
      selectedCategory.preload ||
      isLoaded(selectedCategory),
    variables: selectedCategory?.data,
    context: { doNotRetry: true },
    fetchPolicy: "no-cache",
  });

  const { data: defaultSearchData, error: defaultSearchError } =
    useQueryWithPreviousData(searchListingsQuery, {
      variables: {
        searchFilters: {
          ...getActiveLearnerFilters(activeLearner),
          timeZone,
        },
        limit,
        skipRating: false,
        withRecentReviews: false,
        useCase: SearchUseCase.Feed,
      },
      fetchPolicy: "cache-and-network",
    });
  const defaultSearchUid = defaultSearchData?.searchListings?.searchUid;

  React.useEffect(() => {
    preloadCategories.forEach(category => {
      fetchPreloadCategoryQuery(client, {
        category,
        skip: isLoaded(category),
        setResults,
      });
    });
  }, [preloadCategories, client, isLoaded]);

  React.useEffect(() => {
    if (selectedCategory && data && !loading) {
      const nestedData = getNestedData(data, selectedCategory.path);
      const searchUid =
        "searchUid" in selectedCategory.data &&
        typeof selectedCategory.data.searchUid === "string"
          ? selectedCategory.data.searchUid
          : undefined;
      setResults(prev => ({
        ...prev,
        [selectedCategory.id]: { listings: nestedData, searchUid },
      }));
    }
  }, [selectedCategory, data, loading]);

  const fallbackData = React.useMemo(
    () => defaultSearchData?.searchListings?.listings || [],
    [defaultSearchData],
  );

  const preloadFinished = React.useMemo(
    () =>
      preloadCategories.every(c => results[c.id]) &&
      (fallbackData.length > 0 || defaultSearchError),
    [preloadCategories, results, fallbackData, defaultSearchError],
  );

  return {
    loading,
    data: results,
    preloadFinished,
    fallbackData: { listings: fallbackData, searchUid: defaultSearchUid },
  };
};

const fetchPreloadCategoryQuery = async (
  client: ApolloClient<any>,
  {
    category,
    skip,
    setResults,
  }: {
    category: FeaturedCategory;
    skip?: boolean;
    setResults: React.Dispatch<
      React.SetStateAction<ParentDashboardFeaturedQueriesResults>
    >;
  },
) => {
  if (skip) {
    return;
  }
  const searchUid =
    "searchUid" in category.data && typeof category.data.searchUid === "string"
      ? category.data.searchUid
      : undefined;
  try {
    const res = await client.query({
      // @ts-expect-error query type
      query: category.query,
      variables: category.data,
      fetchPolicy: "no-cache",
      context: { doNotRetry: true },
    });
    const nestedData = getNestedData(res.data, category.path);
    setResults(prev => ({
      ...prev,
      [category.id]: { listings: nestedData, searchUid },
    }));
  } catch {
    setResults(prev => ({
      ...prev,
      [category.id]: { listings: [], searchUid },
    }));
  }
};

export default useParentDashboardFeaturedQueries;
